import React from "react";
import { motion } from "framer-motion";
import {
  Shield,
  Zap,
  Award,
  Users,
  Leaf,
  Target,
  Eye,
  CheckCircle2,
  ArrowRight,
  Mail,
  Phone,
  Sparkles,
  Rocket,
  Star,
  Building2
} from "lucide-react";
import { Link } from "react-router-dom";

const AboutUs = () => {
  const values = [
    { icon: Shield, title: "Uncompromising Quality", desc: "Rigorous testing and premium materials for long-lasting infrastructure." },
    { icon: Zap, title: "Innovation First", desc: "Evolving technology to deliver the highest efficiency and smart energy." },
    { icon: Leaf, title: "Sustainable Impact", desc: "Reducing carbon footprints to empower a greener future for India." },
    { icon: Users, title: "Customer-Centric", desc: "Standing by our clients from initial consultation to lifetime support." },
  ];

  const reasons = [
    "Premium Tier-1 solar modules",
    "Advanced German-grade engineering",
    "Dedicated lifecycle customer service",
    "100% transparent cost structures",
    "Tailored solutions for every roof",
    "Direct subsidy liaisoning",
  ];

  const milestones = [
    { year: "2015", title: "Company Founded", desc: "Started operations with a vision to electrify India sustainably." },
    { year: "2018", title: "500+ Installations", desc: "Crossed 500 residential rooftop setups across central India." },
    { year: "2021", title: "MW Scale Operations", desc: "Entered the corporate megawatt-scale infrastructure sector." },
    { year: "2024", title: "Pan-India Expansion", desc: "Successfully deployed grid solutions across 10+ states nationally." },
  ];

  
  return (
    <div className="bg-[#F8FAFC] antialiased selection:bg-[#F7941D]/20 selection:text-[#0D3F7F] font-sans">
      
      {/* 🌟 1. DARK PREMIUM HERO SECTION 🌟 */}
      <section className="relative overflow-hidden bg-[#050B14] pt-24 pb-12 sm:pt-28 sm:pb-16 lg:pt-32 lg:pb-24 border-b border-[#1E5CB3]/20">
        
        {/* Ambient Glow */}
        <div className="absolute top-0 right-1/4 w-[250px] sm:w-[400px] h-[250px] sm:h-[400px] bg-[#F7941D]/15 rounded-full blur-[80px] sm:blur-[120px] pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[250px] sm:w-[400px] h-[250px] sm:h-[400px] bg-[#1E5CB3]/20 rounded-full blur-[80px] sm:blur-[120px] pointer-events-none" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `radial-gradient(#ffffff 1px, transparent 1px)`, backgroundSize: '24px 24px' }} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            
            {/* ⚡ Left Content (Text & Badges) */}
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="lg:col-span-7 text-center lg:text-left flex flex-col items-center lg:items-start"
            >
              <div className="inline-flex items-center gap-2 bg-[#0D3F7F]/40 border border-[#1E5CB3]/40 px-3.5 py-1 sm:py-1.5 rounded-full mb-3 sm:mb-4 backdrop-blur-md shadow-sm">
                <Building2 className="w-3.5 h-3.5 text-[#F7941D]" />
                <span className="text-[9px] sm:text-[10px] font-bold tracking-widest text-[#F0F7FF] uppercase">
                  About Devdeepam Solar
                </span>
              </div>

              <h1 className="text-3xl xs:text-4xl sm:text-5xl md:text-6xl font-black text-white tracking-tight leading-[1.1] mb-3 sm:mb-4">
                Powering A <br className="hidden lg:block" />
                <span className="bg-gradient-to-r from-[#F7941D] to-[#FFC107] bg-clip-text text-transparent drop-shadow-sm">
                  Greener Tomorrow.
                </span>
              </h1>

              <p className="text-slate-300 text-xs sm:text-sm md:text-[14px] leading-relaxed max-w-lg mb-5 sm:mb-6 font-medium px-1 sm:px-0">
                We deliver exceptional, high-grade solar infrastructure engineered for maximum efficiency. From premium monocrystalline panels to intelligent inverters, we craft sustainability.
              </p>

              {/* Badges */}
              <div className="flex flex-wrap justify-center lg:justify-start gap-2 sm:gap-2.5 w-full mb-5 sm:mb-6">
                {['15+ Years Legacy', '2,500+ Clients', 'Tier-1 Modules'].map((badge, i) => (
                  <div key={i} className="flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-full px-2.5 sm:px-3 py-1 sm:py-1.5 text-[10px] sm:text-[11px] font-bold text-slate-300 backdrop-blur-sm">
                    <CheckCircle2 className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-[#F7941D] shrink-0" />
                    <span>{badge}</span>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <div className="w-full max-w-xs mx-auto lg:mx-0">
                <Link to="/contact" className="group w-full bg-gradient-to-r from-[#F7941D] to-[#E67A00] text-white text-center py-3.5 rounded-full font-bold text-xs sm:text-[13px] uppercase tracking-wider shadow-[0_8px_20px_rgba(247,148,29,0.3)] hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2">
                  <span>Contact Our Team</span>
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </motion.div>

            {/* ⚡ Right Image Panel (Fully Responsive Height) */}
            <motion.div
              initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
              className="lg:col-span-5 relative w-full"
            >
              {/* Outer Glow */}
              <div className="absolute -inset-1 sm:-inset-1.5 bg-gradient-to-tr from-[#0D3F7F] via-[#F7941D] to-[#FFC107] rounded-2xl sm:rounded-[1.5rem] blur-md sm:blur-lg opacity-30" />
              
              <div className="relative rounded-2xl sm:rounded-[1.5rem] overflow-hidden shadow-2xl border border-white/10 p-1 sm:p-1.5 bg-[#050B14]">
                <div className="relative w-full h-[260px] sm:h-[340px] lg:h-[360px] overflow-hidden rounded-xl">
                  <img
                    src="https://images.unsplash.com/photo-1509391366360-2e959784a276?auto=format&fit=crop&w=800&q=90"
                    alt="Solar Infrastructure"
                    className="w-full h-full object-cover opacity-90 hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050B14] via-transparent to-transparent opacity-80" />

                  {/* Top-right Rating Badge */}
                  <div className="absolute top-3 right-3 sm:top-4 sm:right-4 bg-white/95 backdrop-blur-md border border-white/20 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full flex items-center gap-1.5 shadow-lg">
                    <Star className="w-3 h-3 sm:w-3.5 sm:h-3.5 fill-[#FFC107] text-[#FFC107]" />
                    <span className="text-[10px] sm:text-[11px] font-black text-[#0D3F7F]">4.9/5</span>
                  </div>

                  {/* Bottom Floating Card */}
                  <div className="absolute bottom-3 left-3 right-3 sm:bottom-4 sm:left-4 sm:right-4 bg-white/95 backdrop-blur-md border border-white/20 rounded-xl p-3 sm:p-4 shadow-xl flex items-center gap-2.5 sm:gap-3">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-br from-[#F7941D] to-[#FFC107] flex items-center justify-center text-white shrink-0 shadow-md">
                      <Award className="w-4 h-4 sm:w-5 sm:h-5" />
                    </div>
                    <div className="text-left min-w-0">
                      <div className="text-[11px] sm:text-[12px] font-black text-[#0F172A]">India's Trusted Partner</div>
                      <div className="text-[9px] sm:text-[10px] text-slate-500 font-bold mt-0.5">Powering 10+ states cleanly</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* 🌟 3. MISSION & VISION (Bento Style) 🌟 */}
      <section className="py-10 sm:py-12 lg:py-16 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-8 sm:mb-10">
            <span className="text-[9px] sm:text-[10px] font-bold tracking-widest text-[#0D3F7F] uppercase bg-[#F0F7FF] px-3 py-1 rounded-full border border-[#1E5CB3]/20">
              Purpose & Future
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-[#0F172A] tracking-tight mt-3">
              Our Mission & <span className="bg-gradient-to-r from-[#F7941D] to-[#FFC107] bg-clip-text text-transparent">Vision</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="bg-white border border-slate-200 rounded-2xl sm:rounded-[1.5rem] p-5 sm:p-6 lg:p-8 shadow-sm hover:shadow-[0_15px_30px_rgba(13,63,127,0.05)] hover:border-[#1E5CB3]/30 transition-all duration-300 group"
            >
              <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-[#F0F7FF] border border-[#1E5CB3]/10 flex items-center justify-center text-[#0D3F7F] mb-4 sm:mb-5 group-hover:bg-[#F7941D] group-hover:text-white transition-colors duration-300">
                <Target className="w-5 h-5" />
              </div>
              <h3 className="text-lg sm:text-xl font-black text-[#0F172A] tracking-tight mb-2 group-hover:text-[#0D3F7F] transition-colors">Our Mission</h3>
              <p className="text-slate-500 text-xs sm:text-sm md:text-[14px] leading-relaxed font-medium">
                To accelerate India's transition to sustainable renewable energy by delivering intelligent, grid-optimized solar infrastructure that empowers every home and business.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
              className="bg-white border border-slate-200 rounded-2xl sm:rounded-[1.5rem] p-5 sm:p-6 lg:p-8 shadow-sm hover:shadow-[0_15px_30px_rgba(247,148,29,0.05)] hover:border-[#F7941D]/30 transition-all duration-300 group"
            >
              <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-[#FFF9F0] border border-[#F7941D]/10 flex items-center justify-center text-[#F7941D] mb-4 sm:mb-5 group-hover:bg-[#0D3F7F] group-hover:text-white transition-colors duration-300">
                <Eye className="w-5 h-5" />
              </div>
              <h3 className="text-lg sm:text-xl font-black text-[#0F172A] tracking-tight mb-2 group-hover:text-[#F7941D] transition-colors">Our Vision</h3>
              <p className="text-slate-500 text-xs sm:text-sm md:text-[14px] leading-relaxed font-medium">
                To be the most trusted solar engineering firm in India by 2030, pioneering innovative green technologies that make clean energy accessible and affordable for all.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 🌟 4. CORE VALUES GRID 🌟 */}
      <section className="py-10 sm:py-12 lg:py-16 bg-white border-y border-slate-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-8 sm:mb-10">
            <h2 className="text-2xl sm:text-3xl font-black text-[#0F172A] tracking-tight">
              Our <span className="text-[#0D3F7F]">Core Values</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {values.map((item, idx) => (
              <div
                key={idx}
                className="bg-[#F8FAFC] border border-slate-200/80 rounded-2xl p-4 sm:p-5 hover:bg-white hover:shadow-[0_10px_20px_rgba(13,63,127,0.05)] hover:border-[#1E5CB3]/30 transition-all duration-300 group"
              >
                <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-[#0D3F7F] mb-3 sm:mb-4 group-hover:bg-[#F7941D] group-hover:border-[#F7941D] group-hover:text-white transition-colors duration-300 shadow-sm">
                  <item.icon className="w-4 h-4" />
                </div>
                <h4 className="text-[#0F172A] font-bold text-xs sm:text-[14px] mb-1.5 group-hover:text-[#0D3F7F] transition-colors">
                  {item.title}
                </h4>
                <p className="text-slate-500 text-[11px] sm:text-[12px] leading-relaxed font-medium">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 🌟 5. JOURNEY / MILESTONES TIMELINE (Dark Theme) 🌟 */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[#050B14] relative overflow-hidden">
        <div className="absolute top-0 right-1/4 w-[200px] sm:w-[300px] h-[200px] sm:h-[300px] bg-[#1E5CB3]/20 rounded-full blur-[80px] sm:blur-[100px] pointer-events-none" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(90deg, #ffffff 1px, transparent 1px)`, backgroundSize: '30px 30px' }} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-10 items-start">
            
            <div className="lg:col-span-5 lg:sticky lg:top-28 text-left space-y-3 sm:space-y-4">
              <span className="text-[9px] sm:text-[10px] font-bold tracking-widest text-[#FFC107] uppercase bg-[#FFC107]/10 px-3 py-1 rounded-full border border-[#FFC107]/20 inline-block">
                Our Journey
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-white tracking-tight">
                A Decade of <br />
                <span className="bg-gradient-to-r from-[#F7941D] to-[#FFC107] bg-clip-text text-transparent">Solar Engineering</span>
              </h2>
              <p className="text-slate-400 text-xs sm:text-[13px] font-medium leading-relaxed max-w-sm">
                From our humble beginnings to pan-India operations, every milestone reinforces our commitment to clean energy.
              </p>
            </div>

            <div className="lg:col-span-7 relative pt-4 lg:pt-0">
              <div className="absolute left-[22px] sm:left-[23px] top-2 bottom-2 w-[2px] bg-white/10 z-0">
                <motion.div 
                  initial={{ top: "-10%" }} animate={{ top: "110%" }} transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                  className="absolute left-0 w-full h-24 bg-gradient-to-b from-transparent via-[#F7941D] to-transparent shadow-[0_0_8px_#F7941D]"
                />
              </div>

              <div className="space-y-5 sm:space-y-6 relative z-10">
                {milestones.map((item, idx) => (
                  <div key={idx} className="flex gap-3 sm:gap-4 lg:gap-6 items-start group">
                    <div className="flex-shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-[#050B14] border-2 border-white/20 flex items-center justify-center text-xs sm:text-[13px] font-black text-white shadow-lg group-hover:border-[#F7941D] group-hover:text-[#F7941D] transition-colors">
                      {idx + 1}
                    </div>
                    <div className="flex-1 bg-white/5 border border-white/10 group-hover:bg-white/10 group-hover:border-[#F7941D]/40 rounded-2xl p-4 sm:p-5 backdrop-blur-sm transition-all min-w-0">
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                        <h4 className="text-sm sm:text-[15px] font-bold text-white group-hover:text-[#FFC107] transition-colors">
                          {item.title}
                        </h4>
                        <span className="text-[9px] sm:text-[10px] font-black tracking-widest text-[#0D3F7F] uppercase bg-[#F0F7FF] px-2 py-0.5 rounded-md">
                          {item.year}
                        </span>
                      </div>
                      <p className="text-slate-400 text-[11px] sm:text-[12px] font-medium leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 🌟 6. WHY CHOOSE US + COMPACT CONTACT CTA 🌟 */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[#F4F7FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 sm:gap-6">
            
            <div className="lg:col-span-2 bg-white border border-slate-200/80 rounded-2xl sm:rounded-[1.5rem] p-5 sm:p-6 lg:p-8 shadow-sm">
              <div className="inline-flex items-center gap-2 bg-[#F0F7FF] border border-[#1E5CB3]/20 px-3 py-1 rounded-full mb-3 sm:mb-4 shadow-xs">
                <Sparkles className="w-3.5 h-3.5 text-[#F7941D]" />
                <span className="text-[9px] sm:text-[10px] font-bold tracking-widest text-[#0D3F7F] uppercase">Why Choose Us</span>
              </div>
              
              <h3 className="text-xl sm:text-2xl md:text-3xl font-black text-[#0F172A] mb-4 sm:mb-5 tracking-tight">
                Reasons to Trust <br className="hidden sm:block" />
                <span className="text-[#F7941D]">Devdeepam Solar</span>
              </h3>

              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3 mb-5 sm:mb-6">
                {reasons.map((reason, idx) => (
                  <li key={idx} className="flex items-start gap-2.5 text-slate-600 text-xs sm:text-[13px] font-medium">
                    <CheckCircle2 className="w-4 h-4 text-[#1E5CB3] shrink-0 mt-0.5" />
                    <span>{reason}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-[#0D3F7F] hover:bg-[#F7941D] text-white text-xs sm:text-[12px] font-bold px-5 sm:px-6 py-2.5 sm:py-3 rounded-full transition-colors shadow-sm"
              >
                <span>Explore Contact Options</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            <div className="bg-gradient-to-br from-[#0D3F7F] to-[#050B14] border border-[#1E5CB3]/40 rounded-2xl sm:rounded-[1.5rem] p-5 sm:p-6 lg:p-8 shadow-xl flex flex-col justify-between relative overflow-hidden">
              <div className="absolute -right-10 -bottom-10 w-32 sm:w-40 h-32 sm:h-40 bg-[#F7941D]/20 rounded-full blur-[50px] sm:blur-[60px]" />
              
              <div className="relative z-10">
                <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center text-[#F7941D] mb-3 sm:mb-4">
                  <Rocket className="w-4 h-4" />
                </div>
                
                <h4 className="text-white font-black text-lg sm:text-xl tracking-tight mb-1">Get In Touch</h4>
                <p className="text-slate-400 text-[11px] sm:text-[12px] font-medium mb-4 sm:mb-5">
                  Ready to go solar? Our team is here to help you.
                </p>

                <div className="space-y-2.5 sm:space-y-3 mb-5 sm:mb-6">
                  <a href="tel:0 97557 94514" className="flex items-center gap-3 text-white hover:text-[#F7941D] transition-colors group/link">
                    <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center shrink-0 group-hover/link:bg-[#F7941D]/20 transition-colors">
                      <Phone className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-xs sm:text-[13px] font-bold">0 97557 94514</span>
                  </a>
                  <a href="mailto:info@devdeepamsolar.com" className="flex items-center gap-3 text-white hover:text-[#F7941D] transition-colors group/link min-w-0">
                    <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center shrink-0 group-hover/link:bg-[#F7941D]/20 transition-colors">
                      <Mail className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-xs sm:text-[13px] font-bold truncate min-w-0">info@devdeepamsolar.com</span>
                  </a>
                </div>

                <Link
                  to="/contact"
                  className="w-full bg-white text-[#0F172A] hover:bg-[#F7941D] hover:text-white text-xs sm:text-[12px] font-bold py-2.5 sm:py-3 rounded-full flex items-center justify-center gap-2 transition-colors"
                >
                  Send Message <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default AboutUs;